import { useEffect, useState, useCallback } from 'react';
import { MdRefresh } from "react-icons/md";
import { useAppSelector } from '../../store/hooks.js';
import { selectTradingProviders } from '../../store/settingsSlice';
import { ProviderFactory, TRADING_PROVIDERS } from '../../services/providers';
import type { TradingProviderType } from '../../services/providers';
import type { Funds } from '@ocktra/shared-types';

interface FundsData {
    providerName: string;
    connectionId: string;
    funds: Funds;
    error?: string;
}

const Funds = () => {
    const [fundsData, setFundsData] = useState<FundsData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const tradingProviders = useAppSelector(selectTradingProviders);

    const fetchFunds = useCallback(async () => {
        try {
            setIsLoading(true);
            const connectedProviders = tradingProviders.filter(p => p.status === 'connected');

            if (connectedProviders.length === 0) {
                setFundsData([]);
                setIsLoading(false);
                return;
            }

            const allFundsPromises = connectedProviders.map(async (provider) => {
                try {
                    const providerInstance = ProviderFactory.getTradingProvider(
                        provider.providerName as TradingProviderType
                    );

                    if (!providerInstance) {
                        return {
                            providerName: provider.providerName,
                            connectionId: provider.id,
                            funds: { availableFunds: 0, utilizedFunds: 0, totalFunds: 0 },
                            error: 'Provider not available'
                        };
                    }

                    providerInstance.updateConnectionDetails(provider);
                    const funds = await providerInstance.getFunds();

                    return {
                        providerName: provider.providerName,
                        connectionId: provider.id,
                        funds: funds
                    };
                } catch (err) {
                    console.error(`Error fetching funds from ${provider.providerName}:`, err);
                    return {
                        providerName: provider.providerName,
                        connectionId: provider.id,
                        funds: { availableFunds: 0, utilizedFunds: 0, totalFunds: 0 },
                        error: err instanceof Error ? err.message : 'Failed to fetch'
                    };
                }
            });

            const fundsResults = await Promise.all(allFundsPromises);
            setFundsData(fundsResults);
        } catch (err) {
            console.error('[Funds] Error fetching funds:', err);
        } finally {
            setIsLoading(false);
        }
    }, [tradingProviders]);

    useEffect(() => {
        fetchFunds();
    }, [fetchFunds]);

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const getProviderIcon = (providerType: string) => {
        const icons: Record<string, string> = {
            papertrade: '🎮',
            kite: '🟢',
        };
        return icons[providerType] || '📊';
    };

    // Calculate aggregated totals
    const aggregateTotals = () => {
        const total = fundsData.reduce((sum, fund) => sum + (fund.error ? 0 : fund.funds.totalFunds), 0);
        const available = fundsData.reduce((sum, fund) => sum + (fund.error ? 0 : fund.funds.availableFunds), 0);
        return { total, available };
    };

    return (
        <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--color-surface)'
        }}>
            {/* Header */}
            <div style={{
                padding: 'var(--spacing-sm) var(--spacing-md)',
                borderBottom: '1px solid var(--color-border-light)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'var(--color-background)',
                flexShrink: 0
            }}>
                <h3 style={{
                    margin: 0,
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)'
                }}>
                    Funds
                </h3>
                <button
                    onClick={fetchFunds}
                    disabled={isLoading}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        padding: 'var(--spacing-xs)',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'var(--color-text-secondary)',
                        opacity: isLoading ? 0.5 : 1
                    }}
                    title="Refresh"
                >
                    <MdRefresh
                        size={18}
                        style={{
                            animation: isLoading ? 'spin 1s linear infinite' : 'none'
                        }}
                    />
                </button>
            </div>

            {/* Content */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden'
            }}>
                {isLoading && fundsData.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        color: 'var(--color-text-secondary)',
                        fontSize: '0.875rem'
                    }}>
                        Loading funds...
                    </div>
                ) : fundsData.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        padding: 'var(--spacing-lg)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '2rem',
                            marginBottom: 'var(--spacing-sm)'
                        }}>
                            💰
                        </div>
                        <div style={{
                            color: 'var(--color-text-secondary)',
                            fontSize: '0.875rem'
                        }}>
                            No trading provider connected
                        </div>
                    </div>
                ) : (
                    <div style={{ padding: 'var(--spacing-md)' }}>
                        {/* Aggregated Summary */}
                        <div style={{
                            backgroundColor: 'var(--color-primary-50)',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-md)',
                            border: '1px solid var(--color-primary-200)'
                        }}>
                            <div style={{
                                fontSize: '0.6875rem',
                                color: 'var(--color-primary-700)',
                                marginBottom: 'var(--spacing-sm)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: '600'
                            }}>
                                Total Balance
                            </div>
                            <div style={{
                                fontSize: '1.75rem',
                                fontWeight: '700',
                                color: 'var(--color-primary-900)',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                {formatCurrency(aggregateTotals().total)}
                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: 'var(--spacing-md)'
                            }}>
                                <div>
                                    <div style={{
                                        fontSize: '0.6875rem',
                                        color: 'var(--color-primary-600)',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Available
                                    </div>
                                    <div style={{
                                        fontSize: '1.125rem',
                                        fontWeight: '700',
                                        color: 'var(--color-success-700)'
                                    }}>
                                        {formatCurrency(aggregateTotals().available)}
                                    </div>
                                </div>
                                <div>
                                    <div style={{
                                        fontSize: '0.6875rem',
                                        color: 'var(--color-primary-600)',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Utilized
                                    </div>
                                    <div style={{
                                        fontSize: '1.125rem',
                                        fontWeight: '700',
                                        color: 'var(--color-text-primary)'
                                    }}>
                                        {formatCurrency(aggregateTotals().total - aggregateTotals().available)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Provider Breakdown */}
                        <div style={{
                            fontSize: '0.6875rem',
                            color: 'var(--color-text-secondary)',
                            marginBottom: 'var(--spacing-sm)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: '600',
                            paddingLeft: '4px'
                        }}>
                            Provider Breakdown
                        </div>

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--spacing-xs)'
                        }}>
                            {fundsData.map((fund) => (
                                <div
                                    key={fund.connectionId}
                                    style={{
                                        backgroundColor: 'var(--color-background)',
                                        borderRadius: 'var(--radius-md)',
                                        padding: 'var(--spacing-sm) var(--spacing-md)',
                                        border: '1px solid var(--color-border-light)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        transition: 'all 150ms ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
                                        e.currentTarget.style.borderColor = 'var(--color-border)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--color-background)';
                                        e.currentTarget.style.borderColor = 'var(--color-border-light)';
                                    }}
                                >
                                    {fund.error ? (
                                        <>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                                                <span style={{ fontSize: '1rem' }}>
                                                    {getProviderIcon(fund.providerName)}
                                                </span>
                                                <div>
                                                    <div style={{
                                                        fontSize: '0.8125rem',
                                                        fontWeight: '600',
                                                        color: 'var(--color-text-primary)'
                                                    }}>
                                                        {TRADING_PROVIDERS[fund.providerName as TradingProviderType]?.name || fund.providerName}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.6875rem',
                                                        color: 'var(--color-danger-600)',
                                                        marginTop: '2px'
                                                    }}>
                                                        {fund.error}
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                                                <span style={{ fontSize: '1rem' }}>
                                                    {getProviderIcon(fund.providerName)}
                                                </span>
                                                <div>
                                                    <div style={{
                                                        fontSize: '0.8125rem',
                                                        fontWeight: '600',
                                                        color: 'var(--color-text-primary)',
                                                        marginBottom: '2px'
                                                    }}>
                                                        {TRADING_PROVIDERS[fund.providerName as TradingProviderType]?.name || fund.providerName}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.6875rem',
                                                        color: 'var(--color-text-secondary)'
                                                    }}>
                                                        Total: {formatCurrency(fund.funds.totalFunds)}
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', gap: 'var(--spacing-lg)', alignItems: 'center' }}>
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{
                                                        fontSize: '0.6875rem',
                                                        color: 'var(--color-text-secondary)',
                                                        marginBottom: '2px'
                                                    }}>
                                                        Available
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.8125rem',
                                                        fontWeight: '700',
                                                        color: 'var(--color-success-600)'
                                                    }}>
                                                        {formatCurrency(fund.funds.availableFunds)}
                                                    </div>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{
                                                        fontSize: '0.6875rem',
                                                        color: 'var(--color-text-secondary)',
                                                        marginBottom: '2px'
                                                    }}>
                                                        Utilized
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.8125rem',
                                                        fontWeight: '700',
                                                        color: 'var(--color-text-primary)'
                                                    }}>
                                                        {formatCurrency(fund.funds.utilizedFunds)}
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
            `}</style>
        </div>
    );
};

export default Funds;
